import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, FileText, Loader2, Trash2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Transaction } from "@/context/FinanceContext";
import {
  TransactionAttachment,
  useTransactionAttachments,
} from "@/hooks/finance/useTransactionAttachments";

interface AttachmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction | null;
  attachmentsApi: ReturnType<typeof useTransactionAttachments>;
  canEdit?: boolean;
}

const formatSize = (bytes?: number | null) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AttachmentsDialog: React.FC<AttachmentsDialogProps> = ({
  open,
  onOpenChange,
  transaction,
  attachmentsApi,
  canEdit = true,
}) => {
  const { listAttachments, uploadAttachments, removeAttachment, openAttachment } = attachmentsApi;
  const [items, setItems] = useState<TransactionAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    if (!transaction) return;
    setLoading(true);
    const data = await listAttachments(transaction.id);
    setItems(data);
    setLoading(false);
  }, [transaction, listAttachments]);

  useEffect(() => {
    if (open && transaction) reload();
    if (!open) setItems([]);
  }, [open, transaction, reload]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !transaction) return;
    setBusy(true);
    await uploadAttachments(transaction.id, Array.from(files));
    await reload();
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDelete = async (att: TransactionAttachment) => {
    setBusy(true);
    await removeAttachment(att);
    await reload();
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Comprovantes</DialogTitle>
          <DialogDescription className="truncate">
            {transaction?.description || "Lançamento"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {canEdit && (
            <div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
              <Button
                variant="outline"
                className="w-full"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Anexar comprovante (PDF, JPG, PNG · até 10 MB)
              </Button>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Carregando...
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Nenhum comprovante anexado.
              </p>
            ) : (
              items.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-2 border rounded-md p-2 bg-card"
                >
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate" title={att.file_name}>
                      {att.file_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatSize(att.size_bytes)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Visualizar"
                    onClick={() => openAttachment(att)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Baixar"
                    onClick={() => openAttachment(att, true)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Excluir"
                      disabled={busy}
                      onClick={() => handleDelete(att)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
